/**
 * „Descarca raspunsurile” pe fisele „Exercitii cu clasa”: PDF-ul cu raspunsuri e cheia temei online, deci
 * URL-ul lui nu ajunge niciodata in frontend (LessonDTO are doar `areRaspunsuriPdf`). Singura cale e
 * backendul, `GET /api/lessons/{id}/raspunsuri-pdf` (PROFESOR/ADMIN/DBEST), care il trimite ca atasament.
 *
 * Spre deosebire de `descarcaExercitiiPdf`, nu exista ramura „tab nou”: n-avem un URL de deschis. La esec
 * intoarcem `'esuat'`, iar butonul arata un mesaj; nu deschidem nimic dupa `await` (Safari l-ar bloca).
 * Dependintele se injecteaza, ca in helper-ul frate: vitest ruleaza fara DOM.
 */
import { descarcaRaspunsuriPdf as descarcaRaspunsuriPdfSdk } from '@/api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';
import { numeFisier } from '@/domains/auth/utils/protocol-descarcare';
import { numePdfDinTitlu } from './descarca-exercitii-pdf';

export interface DepDescarcareRaspunsuri {
  /** Cere PDF-ul de la backend; arunca la orice eroare. */
  cere: (lessonId: string) => Promise<{ blob: Blob; dispozitie: string | null }>;
  /** Salveaza blob-ul ca fisier, cu numele dat. */
  salveaza: (blob: Blob, nume: string) => void;
}

export type RezultatDescarcareRaspunsuri = 'descarcat' | 'esuat';

async function cereDinBackend(lessonId: string): Promise<{ blob: Blob; dispozitie: string | null }> {
  const raspuns = await descarcaRaspunsuriPdfSdk({ path: { id: lessonId } });
  const blob = handleResponse(raspuns) as Blob;
  const antete = (raspuns as { headers?: Record<string, unknown> }).headers;
  const dispozitie = antete?.['content-disposition'];
  return { blob, dispozitie: typeof dispozitie === 'string' ? dispozitie : null };
}

function salveazaInBrowser(blob: Blob, nume: string): void {
  const url = URL.createObjectURL(blob);
  const legatura = document.createElement('a');
  legatura.href = url;
  legatura.download = nume;
  document.body.appendChild(legatura);
  legatura.click();
  document.body.removeChild(legatura);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const DEP_IMPLICITE: DepDescarcareRaspunsuri = { cere: cereDinBackend, salveaza: salveazaInBrowser };

/** Ca `depDescarcareCu`: aceeasi salvare, alta sursa (linkul de colaborator). */
export function depRaspunsuriCu(cere: DepDescarcareRaspunsuri['cere']): DepDescarcareRaspunsuri {
  return { ...DEP_IMPLICITE, cere };
}

/** Numele de rezerva, cand backendul nu trimite Content-Disposition. */
export function numeRaspunsuriDinTitlu(titlu: string): string {
  const nume = numePdfDinTitlu(titlu);
  return nume === 'exercitii.pdf' ? 'raspunsuri.pdf' : `Raspunsuri - ${nume}`;
}

export async function descarcaRaspunsuriPdf(
  lessonId: string,
  titlu: string,
  dep: DepDescarcareRaspunsuri = DEP_IMPLICITE
): Promise<RezultatDescarcareRaspunsuri> {
  try {
    const { blob, dispozitie } = await dep.cere(lessonId);
    if (!blob || blob.size === 0) return 'esuat';
    dep.salveaza(blob, numeFisier(dispozitie, numeRaspunsuriDinTitlu(titlu)));
    return 'descarcat';
  } catch {
    return 'esuat';
  }
}
